import { MouseEvent, useState } from "react";
import Link from "@mui/material/Link";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SaleDepositCreditDebitNoteAdjustment } from "../../types/sale-deposit.type";
import { formatSignedBaht } from "../CreditDebitNote/reportFigures";
import NoteAdjustmentLines from "./NoteAdjustmentLines";
import { NET_OF_NOTES_HINT } from "./transfer.utils";

const NOTE_LINES_TITLE = "ใบลดหนี้ / ใบเพิ่มหนี้ที่รวมในยอดที่ต้องโอน";
const NOTE_LINES_MIN_WIDTH = 280;
const NO_WRAP_SX = { whiteSpace: "nowrap" } as const;
const POPOVER_ANCHOR = { vertical: "bottom", horizontal: "right" } as const;
const POPOVER_TRANSFORM = { vertical: "top", horizontal: "right" } as const;

interface NetOfNotesHintProps {
  /** A deposit's own notes: the hint opens them. */
  adjustment?: SaleDepositCreditDebitNoteAdjustment;
  /** A total's net of the notes, where there are no individual notes to open. */
  totalAmount?: number;
}

/**
 * The caption under a ที่ต้องโอน that is already net of the route-day's cash ใบลดหนี้ /
 * ใบเพิ่มหนี้, with how much they moved it — so a figure that no longer equals เงินสด + ฝากเก็บ −
 * ค่าใช้จ่าย is not mistaken for a mistake. It is drawn only when there are notes; without any
 * the cell reads exactly as before.
 *
 * A deposit row passes its `adjustment` and the caption opens the notes behind it. A total only
 * knows the net, so it passes `totalAmount` and the caption is plain text.
 */
export default function NetOfNotesHint({
  adjustment,
  totalAmount = 0,
}: Readonly<NetOfNotesHintProps>) {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const netAmount = adjustment?.net_amount ?? totalAmount;
  const hasAdjustment = adjustment ? adjustment.lines.length > 0 : netAmount !== 0;
  if (!hasAdjustment) return null;

  // A net of exactly 0 with notes present means a credit note and a debit note cancelled out,
  // so a credit note was still deducted.
  const hint = netAmount > 0 ? NET_OF_NOTES_HINT.added : NET_OF_NOTES_HINT.deducted;
  const caption = `${hint} ${formatSignedBaht(netAmount)}`;
  const captionColor = netAmount < 0 ? "error" : "text.secondary";
  const openLines = (event: MouseEvent<HTMLElement>) =>
    setAnchorElement(event.currentTarget);
  const closeLines = () => setAnchorElement(null);

  const content = adjustment ? (
    <>
      <Link
        component="button"
        type="button"
        variant="caption"
        color="inherit"
        underline="always"
        onClick={openLines}
      >
        {caption}
      </Link>
      <Popover
        open={anchorElement !== null}
        anchorEl={anchorElement}
        onClose={closeLines}
        anchorOrigin={POPOVER_ANCHOR}
        transformOrigin={POPOVER_TRANSFORM}
      >
        <Stack gap={1.5} p={2} minWidth={NOTE_LINES_MIN_WIDTH}>
          <Typography variant="subtitle2" fontWeight={700}>
            {NOTE_LINES_TITLE}
          </Typography>
          <NoteAdjustmentLines adjustment={adjustment} />
        </Stack>
      </Popover>
    </>
  ) : (
    caption
  );

  return (
    <Typography
      component="div"
      variant="caption"
      color={captionColor}
      sx={NO_WRAP_SX}
    >
      {content}
    </Typography>
  );
}
